import { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { ProfileMenu } from "@/components/ProfileMenu";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { NotificationCenter } from "./NotificationCenter";
import { Search, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, isLoading, currentWorkspace, isAgency } = useAuth();

  useEffect(() => {
    if (!isLoading && !session) {
      navigate("/auth");
    }
  }, [session, isLoading, navigate]);

  if (isLoading || !user) {
    return null;
  }

  // Get page title from current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/knowledge") return "Knowledge Base";
    if (path === "/strategy") return "Content Strategy";
    if (path === "/posts") return "Post Generator";
    if (path === "/engagement") return "Engagement Manager";
    if (path === "/settings") return "Settings";
    return "LinkedIn Content AI";
  };

  return (
    <SidebarProvider defaultOpen>
      <div className="min-h-screen flex w-full bg-background">
        <div className="fixed inset-0 gradient-mesh pointer-events-none opacity-30"></div>
        <AppSidebar />
        <SidebarInset className="flex-1 relative">
          {/* Premium Top Bar */}
          <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-4 glass-effect border-b border-border/50 px-6">
            <SidebarTrigger className="-ml-2 hover:bg-accent rounded-lg transition-colors" />
            
            <div className="flex items-center gap-3 flex-1">
              <h2 className="font-bold text-sm text-gradient">{getPageTitle()}</h2>
              
              {/* Workspace Indicator - Only for agency users, not admins */}
              {isAgency && currentWorkspace && (
                <div className="flex items-center gap-2 pl-3 border-l border-border/50">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="secondary" className="font-medium">
                    {currentWorkspace.client_name}
                  </Badge>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Workspace Switcher for Agencies */}
              <WorkspaceSwitcher />
              
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search..." 
                  className="pl-10 w-64 bg-muted/50 border-border/50 focus:bg-muted focus:border-primary/50 transition-all rounded-xl"
                />
              </div>
              
              <NotificationCenter />

              <div className="flex items-center gap-3 pl-3 border-l border-border/50">
                <ProfileMenu user={user} />
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <div className="mx-auto max-w-7xl animate-fade-in">
              {children}
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Layout;
